import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { FcGoogle } from 'react-icons/fc';
import shareVideo from '../assets/share.mp4';
import logo from '../assets/logowhite.png';
import avatar from '../assets/default-avatar.png';
import { jwtDecode } from 'jwt-decode';
import { client } from '../client';

const Registration = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    userName: '',
    email: '',
    password: '',
    image: avatar,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  // Example for Registration.jsx
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('http://localhost:4000/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: form.userName,
          email: form.email,
          password: form.password,
          image: form.image,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Registration failed.');
        setLoading(false);
        return;
      }

      localStorage.setItem('user', JSON.stringify(data.user));
      setLoading(false);
      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      setError('Registration failed. Try again.');
      setLoading(false);
    }
  };
  
  const responseGoogle = (credentialResponse) => {
    const { credential } = credentialResponse;
    if (!credential) {
      setError('Google signup failed. Missing credential.');
      return;
    }

    const decoded = jwtDecode(credential);
    const { name, sub: googleId, picture: imageUrl, email } = decoded || {};

    const doc = {
      _id: googleId,
      _type: 'user',
      userName: name,
      email: email,
      image: imageUrl,
    };

    client.createIfNotExists(doc).then((user) => {
      localStorage.setItem('user', JSON.stringify(user));
      navigate('/', { replace: true });
      window.location.reload();
    });
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_API_TOKEN}>
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-100 via-purple-400 to-pink-100 dark:from-gray-900 dark:via-slate-900 dark:to-blue-900 transition-colors duration-300">
        <div className="relative w-full max-w-md mx-auto rounded-3xl overflow-hidden shadow-2xl">
          <video
            src={shareVideo}
            type="video/mp4"
            loop
            controls={false}
            muted
            autoPlay
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="relative z-10 flex flex-col justify-center items-center min-h-[520px] px-6 py-10 bg-gradient-to-br from-blue-300 via-purple-600 to-pink-300 dark:bg-gray-900/80 backdrop-blur-xl">
            <img src={logo} alt="logo" className="w-40 mb-6 drop-shadow-lg" />
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Create an Account</h2>
            <form className="w-full max-w-xs flex flex-col gap-4" onSubmit={handleSubmit}>
              <input
                type="text"
                name="userName"
                placeholder="Username"
                value={form.userName}
                onChange={handleChange}
                className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                autoComplete="username"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                autoComplete="email"
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                autoComplete="new-password"
              />
              {error && <div className="text-red-500 text-sm">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 rounded-lg shadow hover:from-blue-600 hover:to-purple-600 transition disabled:opacity-60"
              >
                {loading ? 'Registering...' : 'Register'}
              </button>
            </form>

            {/* Google Signup */}
            <div className="w-full max-w-xs my-4">
              <GoogleLogin
                onSuccess={responseGoogle}
                onError={() => setError('Google signup failed')}
                useOneTap
                render={(renderProps) => (
                  <button
                    type="button"
                    className="w-full max-w-xs flex justify-center items-center p-3 rounded-lg cursor-pointer outline-none text-white font-semibold bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition shadow-lg"
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled}
                  >
                    <FcGoogle className="mr-4 text-2xl" /> Sign up with Google
                  </button>
                )}
              />
            </div>

            <button
              onClick={() => navigate('/login')}
              className="mt-6 text-blue-100 dark:text-blue-200 font-semibold hover:underline transition"
            >
              Already have an account? <span className="underline">Login</span>
            </button>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default Registration;